import { AppDispatch, GetState, RootState } from '../store';
import sessionService from '../../services/session.service';
import {
	changeSessionState,
	selectCurrentSessionState,
	selectSessionId,
	StartedStateStatus
} from '../slices/session.slice';
import { reportError } from '../slices/error.slice';
import { sleep } from '../../utils/sleep';
import { finish } from './finish.thunk';
import { FinishedState, ProcessingState, SessionRedirectState, SessionState } from '../../types/session';

const OBSERVATION_REQUEST_DELAY = 1000;

export const observe = () => {
	return async (dispatch: AppDispatch, getState: GetState): Promise<any> => {
		const rootState = getState();

		try {
			const sessionId = getSessionId(rootState);
			const currentState = getCurrentSessionState(rootState);

			const nextState = await sessionService.getSessionState(sessionId);

			if (isProcessingState(nextState) || isSameRedirectionState(currentState, nextState)) {
				await sleep(OBSERVATION_REQUEST_DELAY);
				return await dispatch(observe());
			}

			if (isFinishedState(nextState)) {
				return await dispatch(finish(nextState));
			}

			dispatch(changeSessionState(nextState));
		} catch (error: any) {
			dispatch(
				reportError({
					type: 'TECHNICAL',
					reason: error,
					message: 'We are having technical difficulties. Please try again later.'
				})
			);
		}
	};
};

const getSessionId = (state: RootState): string => {
	const sessionId = selectSessionId(state);
	if (!sessionId) throw new Error('Session id not defined on observe');
	return sessionId;
};

const getCurrentSessionState = (state: RootState) => {
	const sessionState = selectCurrentSessionState(state);
	if (!sessionState) throw new Error('Current session state not defined on observe');
	return sessionState;
};

const isProcessingState = (state: SessionState): state is ProcessingState => {
	return state.status === 'PROCESSING';
};

const isSameRedirectionState = (currentState: StartedStateStatus, nextState: SessionState) => {
	return isRedirectState(nextState) && isCurrentStateRedirect(currentState);
};

const isRedirectState = (state: SessionState): state is SessionRedirectState => {
	return state.status === 'CONSUMER_INPUT_NEEDED' && state.result.type === 'redirect';
};

const isFinishedState = (state: SessionState): state is FinishedState => {
	return state.status === 'FINISHED';
};

const isCurrentStateRedirect = (currentState: StartedStateStatus) => {
	return currentState.status === 'CONSUMER_INPUT_NEEDED' && currentState.action === 'redirect';
};
